<div class="tw-mt-6 lg:tw-mt-12 tw-pb-12 lg:tw-pb-6">
  <div class="tw-flex tw-items-center tw-gap-4 tw-pb-4">
    <h1 class="tw-text-gray-700 tw-text-lg tw-font-bold lg:tw-text-3xl tw-font-inter">Dates & Timings</h1>
    @if (isSavingTimings() || showTimingsSavedIndicator()) {
      <app-saving-indicator
        [state]="isSavingTimings() ? 'saving' : 'saved'"
        (savedDone)="onTimingsSavedIndicatorDone()"
      />
    }
  </div>

  <div class="tw-w-full tw-p-4 tw-bg-white tw-rounded-xl tw-border-gray-200 tw-border tw-inline-flex tw-flex-col tw-justify-start tw-items-start tw-gap-6 tw-overflow-hidden">
    <div class="tw-flex tw-flex-col tw-gap-4 tw-w-full">
      <div class="tw-text-gray-700 tw-text-base md:tw-text-lg tw-font-bold tw-font-inter">
        {{ timezoneLabel() }}
      </div>

      <div class="tw-flex tw-items-center tw-w-full tw-gap-4 tw-flex-wrap">
        <app-date-range-picker
          #dateRangePicker
          [startDate]="dateRange()?.startDate ?? null"
          [endDate]="dateRange()?.endDate ?? null"
          [isIndefinitely]="isIndefinite()"
          [initialVisibleMonthDate]="dateRange()?.startDate ?? apiResetStartDate() ?? null"
          [useModalOnMobile]="true"
          (rangeSelected)="onDateRangeSelected($event)"
        ></app-date-range-picker>
        @if (!showWeeklyHoursEditor()) {
          <div class="tw-hidden lg:tw-block tw-w-px tw-h-6 tw-bg-gray-200 tw-shrink-0"></div>
          <app-time-range-picker
            #timeRangePicker
            [startTime]="timeRange()?.startTime ?? null"
            [endTime]="timeRange()?.endTime ?? null"
            [useModalOnMobile]="true"
            (rangeSelected)="onTimeRangeSelected($event)"
          ></app-time-range-picker>
        }
      </div>

      @if (showWeeklyHoursEditor()) {
        <div class="tw-w-full tw-rounded-xl md:tw-border tw-border-gray-200 tw-bg-white md:tw-p-6 tw-flex tw-flex-col tw-gap-6">
          <app-weekly-hours-editor
            [weekDayOptions]="weekDayOptions"
            [selectedWeekDays]="selectedWeekDays()"
            [dayWeekFullName]="dayWeekFullName"
            [openingHoursMode]="openingHoursMode()"
            [isDisabled]="isOpeningHoursDisabled"
            [sameHoursStartHour]="sameHoursStartHour()"
            [sameHoursStartMinute]="sameHoursStartMinute()"
            [sameHoursEndHour]="sameHoursEndHour()"
            [sameHoursEndMinute]="sameHoursEndMinute()"
            [sameHoursError]="indefiniteSameHoursRangeError()"
            [perDayHours]="perDayHours()"
            [perDayErrorByDay]="perDayErrorByDay()"
            (toggleWeekDay)="toggleWeekDay($event)"
            (openingHoursModeChange)="setOpeningHoursMode($event)"
            (sameHoursChange)="onSameHoursPatch($event)"
            (perDayHoursChange)="updatePerDayHour($event.day, $event.patch)"
          />

          <app-event-timing-exceptions
            [organizationId]="organizationId()"
            [eventId]="eventId()"
            [eventPublicStartDate]="dateRange()?.startDate ?? null"
            [eventPublicEndDate]="dateRange()?.endDate ?? null"
            [allowedWeekDays]="exceptionAllowedWeekDays()"
            [isResetting]="isResettingTimingExceptions()"
            [resetVersion]="timingExceptionsResetVersion()"
          />
        </div>
      }

      @if (showScheduler()) {
        <app-event-scheduler
          [days]="days()"
          [startTime]="schedulerStartTime()"
          [endTime]="schedulerEndTime()"
          [timezoneLabel]="timezoneLabel()"
          [savingDayId]="isSavingDay()"
          [addingSlotPreview]="slotAddSavePreview()"
          (dayToggle)="onDayToggle($event)"
          (noteBlur)="onNoteBlur($event)"
          (slotsChange)="onSlotsChange($event)"
          (addSlot)="onAddSlot($event)"
          (addSlotModalOpen)="onAddSlotModalOpen($event)"
          (slotRemove)="onSlotRemove($event)"
          (slotEditOpen)="onSlotEditOpen($event)"
        ></app-event-scheduler>
      }
    </div>
  </div>

  <div class="tw-mt-4 tw-flex tw-justify-end">
    <button
      type="button"
      class="tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-blue-600 tw-px-5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-white hover:tw-bg-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2"
      (click)="finish.emit()"
    >
      Finish
    </button>
  </div>

  <modal
    [id]="editDayHoursModalId"
    [header]="editDayHoursModalHeader"
    [headerIcon]="editDayHoursHeaderIcon"
    [showHeader]="true"
    [showHeaderDivider]="true"
    [showFooterDivider]="true"
    [heightClass]="'tw-h-auto'"
    [widthClass]="'event-dates-modal-shell--edit-day'"
    [enableBackdropClose]="true"
  >
    <div slot="body" class="tw-flex tw-flex-col tw-gap-6">
      <div class="tw-flex tw-gap-4">
        <app-event-time-picker
          class="tw-flex-1 tw-min-w-0"
          label="Opening Time"
          [flexInRow]="true"
          [hour]="editModalStartHour()"
          [minute]="editModalStartMinute()"
          [hourOptions]="editModalHourOptions"
          [minuteOptions]="editModalMinuteOptions"
          (hourChange)="onEditModalStartHourChange($event)"
          (minuteChange)="onEditModalStartMinuteChange($event)"
        />
        <app-event-time-picker
          class="tw-flex-1 tw-min-w-0"
          label="Closing Time"
          [flexInRow]="true"
          [hour]="editModalEndHour()"
          [minute]="editModalEndMinute()"
          [hourOptions]="editModalHourOptions"
          [minuteOptions]="editModalMinuteOptions"
          (hourChange)="onEditModalEndHourChange($event)"
          (minuteChange)="onEditModalEndMinuteChange($event)"
        />
      </div>

      @if (editModalTimeOrderError(); as err) {
        <p class="tw-text-xs tw-text-red-600 tw-font-medium tw-ml-1 tw-font-inter" role="alert">
          {{ err }}
        </p>
      }

      <div class="tw-flex tw-flex-wrap tw-items-center tw-gap-6 sm:tw-gap-10">
        <label class="tw-inline-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-text-sm tw-text-gray-800 tw-font-inter">
          <input
            type="radio"
            name="editDayHoursScope"
            class="tw-h-4 tw-w-4 tw-border-gray-300 tw-accent-blue-600 focus:tw-ring-blue-500"
            [checked]="editModalScope() === 'day'"
            (change)="editModalScope.set('day')"
          />
          Apply to this day only
        </label>
        <label class="tw-inline-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-text-sm tw-text-gray-800 tw-font-inter">
          <input
            type="radio"
            name="editDayHoursScope"
            class="tw-h-4 tw-w-4 tw-border-gray-300 tw-accent-blue-600 focus:tw-ring-blue-500"
            [checked]="editModalScope() === 'entire'"
            (change)="editModalScope.set('entire')"
          />
          Apply to entire event
        </label>
      </div>
    </div>
    <div slot="footer" class="tw-flex tw-gap-3 tw-px-6 tw-py-4">
      <button
        type="button"
        class="tw-flex-1 tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-border tw-border-gray-200 tw-bg-white tw-py-3 tw-px-4 tw-text-sm tw-font-medium tw-text-gray-900 tw-font-inter hover:tw-bg-gray-50 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-gray-300 focus:tw-ring-offset-2 disabled:tw-opacity-50"
        [disabled]="isSavingEditModal()"
        (click)="resetEditModal()"
      >
        Reset
      </button>
      <button
        type="button"
        class="tw-flex-1 tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-blue-600 tw-py-3 tw-px-4 tw-text-sm tw-font-medium tw-text-white tw-font-inter hover:tw-bg-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2 disabled:tw-opacity-50"
        [disabled]="isSavingEditModal()"
        (click)="saveEditModal()"
      >
        Save
      </button>
    </div>
  </modal>

  <modal
    [id]="addDaySlotModalId"
    [header]="addDaySlotModalHeader"
    [headerIcon]="addDaySlotHeaderIcon"
    [showHeader]="true"
    [showHeaderDivider]="true"
    [showFooterDivider]="true"
    [heightClass]="'tw-h-auto'"
    [widthClass]="'event-dates-modal-shell--add-slot'"
    [enableBackdropClose]="true"
  >
    <div slot="body" class="tw-flex tw-flex-col tw-gap-6">
      <div class="tw-flex tw-flex-col tw-gap-4 sm:tw-flex-row sm:tw-gap-4">
        <app-event-time-picker
          label="Opening Time"
          [flexInRow]="true"
          [hour]="addModalStartHour()"
          [minute]="addModalStartMinute()"
          [hourOptions]="editModalHourOptions"
          [minuteOptions]="editModalMinuteOptions"
          (hourChange)="onAddModalStartHourChange($event)"
          (minuteChange)="onAddModalStartMinuteChange($event)"
        />
        <app-event-time-picker
          label="Closing Time"
          [flexInRow]="true"
          [hour]="addModalEndHour()"
          [minute]="addModalEndMinute()"
          [hourOptions]="editModalHourOptions"
          [minuteOptions]="editModalMinuteOptions"
          (hourChange)="onAddModalEndHourChange($event)"
          (minuteChange)="onAddModalEndMinuteChange($event)"
        />
      </div>
    </div>
    <div slot="footer" class="tw-px-6 tw-py-4">
      <button
        type="button"
        class="tw-w-full tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-blue-600 tw-py-3 tw-px-4 tw-text-sm tw-font-semibold tw-text-white tw-font-inter hover:tw-bg-blue-700 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-500 focus:tw-ring-offset-2 disabled:tw-opacity-50"
        [disabled]="isSavingDay() === addModalDayId() && addModalDayId() !== null"
        (click)="confirmAddSlotModal()"
      >
        Confirm
      </button>
    </div>
  </modal>

  <delete-modal
    [id]="confirmMainRangeModalId"
    [message]="confirmMainRangeModalMessage"
    (onDelete)="confirmMainRangeChanges()"
    (onCancel)="cancelMainRangeChanges()"
  />
</div>
